import { useSearchStore } from '@/store/SearchStore';

export interface CarlDevTools {
  getSteps: () => google.maps.DirectionsStep[] | undefined;
  setPosition: (lat: number, lng: number) => void;
  setPositionToLondon: () => void;
  setPositionToBritishMuseum: () => void;
  setStep: (activeStep: number) => void;
  nextStep: () => void;
  prevStep: () => void;
}

const watchCallbacks: Map<number, (position: GeolocationPosition) => void> = new Map();
let mockPosition: GeolocationPosition | null = null;
let watchIdCounter = 0;

const originalWatchPosition = navigator.geolocation.watchPosition.bind(navigator.geolocation);
const originalGetCurrentPosition = navigator.geolocation.getCurrentPosition.bind(navigator.geolocation);
const originalClearWatch = navigator.geolocation.clearWatch.bind(navigator.geolocation);

export const initDevTools = () => {
  window.carl.dev = {
    getSteps: () => {
      const { selectedRoute } = useSearchStore.getState();
      return selectedRoute?.legs.flatMap((leg) => leg.steps);
    },
    setPosition: (lat: number, lng: number) => {
      mockPosition = {
        coords: {
          latitude: lat,
          longitude: lng,
          accuracy: 10,
          heading: null,
          altitude: null,
          altitudeAccuracy: null,
          speed: null,
          toJSON: () => ({
            latitude: lat,
            longitude: lng,
            accuracy: 10,
            heading: null,
            altitude: null,
            altitudeAccuracy: null,
            speed: null,
          }),
        },
        timestamp: Date.now(),
        toJSON: () => ({
          coords: {
            latitude: lat,
            longitude: lng,
            accuracy: 10,
            heading: null,
            altitude: null,
            altitudeAccuracy: null,
            speed: null,
          },
          timestamp: Date.now(),
        }),
      };

      watchCallbacks.forEach((callback) => callback(mockPosition!));
    },
    setPositionToLondon: () => {
      window.carl.dev?.setPosition(51.5074, -0.1278);
    },
    setPositionToBritishMuseum: () => {
      window.carl.dev?.setPosition(51.5194, -0.1268);
    },
    setStep: (activeStep: number) => {
      const { selectedRoute } = useSearchStore.getState();
      if (!selectedRoute) {
        console.error('No route selected. Please select a route first.');
        return;
      }

      const steps = selectedRoute.legs.flatMap((leg) => leg.steps);
      if (activeStep < 0 || activeStep >= steps.length) {
        console.error(`Invalid step index: ${activeStep}. Valid range: 0-${steps.length - 1}`);
        return;
      }

      const step = steps[activeStep];
      if (!step.polyline) {
        console.error(`Step ${activeStep} has no polyline data`);
        return;
      }
      const { lat, lng } = step.start_point;

      window.carl.dev?.setPosition(lat(), lng());
      console.log(`Position set to start of step ${activeStep} (${lat()}, ${lng()})`);
    },
    nextStep: () => {
      const { activeStep: currentActiveStep, setActiveStep, selectedRoute } = useSearchStore.getState();

      if (!selectedRoute) {
        console.error('No route selected. Please select a route first.');
        return;
      }

      const steps = selectedRoute.legs.flatMap((leg) => leg.steps);
      const nextStepIndex = (currentActiveStep ?? 0) + 1;

      if (nextStepIndex >= steps.length) {
        console.log('Already at the last step');
        return;
      }

      setActiveStep(nextStepIndex);
      window.carl.dev?.setStep(nextStepIndex);
    },
    prevStep: () => {
      const { activeStep: currentActiveStep, setActiveStep, selectedRoute } = useSearchStore.getState();

      if (!selectedRoute) {
        console.error('No route selected. Please select a route first.');
        return;
      }

      const prevStepIndex = (currentActiveStep ?? 0) - 1;

      if (prevStepIndex < 0) {
        console.log('Already at the first step');
        return;
      }

      setActiveStep(prevStepIndex);
      window.carl.dev?.setStep(prevStepIndex);
    },
  };

  navigator.geolocation.watchPosition = function (
    successCallback: PositionCallback,
    errorCallback?: PositionErrorCallback,
    options?: PositionOptions,
  ) {
    if (mockPosition !== null) {
      const id = ++watchIdCounter;
      watchCallbacks.set(id, successCallback);

      setTimeout(() => {
        if (mockPosition) successCallback(mockPosition);
      }, 0);

      return id;
    }

    return originalWatchPosition(successCallback, errorCallback, options);
  };

  navigator.geolocation.getCurrentPosition = function (
    successCallback: PositionCallback,
    errorCallback?: PositionErrorCallback,
    options?: PositionOptions,
  ) {
    if (mockPosition !== null) {
      setTimeout(() => {
        if (mockPosition) successCallback(mockPosition);
      }, 0);
    } else {
      originalGetCurrentPosition(successCallback, errorCallback, options);
    }
  };

  navigator.geolocation.clearWatch = function (watchId: number) {
    if (mockPosition !== null) {
      watchCallbacks.delete(watchId);
    } else {
      originalClearWatch(watchId);
    }
  };

  console.log('Development tools initialized. Use carl.dev command to see options.');
};
